// Parse status, as the Bills list has to ask it.
//
// `parseState` answers "what state is THIS EMAIL's parse in". The Bills list
// holds bv_bill rows and never loads an email, so it needs two extra steps: get
// from a bill to the email that carried its invoice, and answer the question at
// all for a bill whose invoice was uploaded by hand and so has no email.
//
// The ladder itself is NOT re-derived here — parseStateOf stays the one place
// that decides parsed/parsing/stalled/failed, which is the whole point of that
// file existing.

import { parseStateOf, type ParseAttachmentLike, type ParseSubjectLike, type ParseState } from './parseState';

/** A bv_invoice_email row, as far as a bill's parse status is concerned. */
export type InvoiceEmailLike = {
  /** bv_invoice_email.id — what the inbox re-parse endpoint takes. */
  id?: string;
  bill_id?: string | null;
  assigned_at?: string | null;
  attachments?: ParseAttachmentLike[] | null;
};

/** The bv_bill fields that say something about its parse without an email. */
export type BillParseRowLike = {
  actual_parsed_result?: unknown | null;
  invoice_path?: string | null;
};

/**
 * One parse subject per bill, merging every email assigned to it.
 *
 * A consolidated invoice can arrive twice and a re-send lands as a second email
 * on the same bill. Keeping them separate would make "does this bill have a
 * failed parse" a question with two answers.
 */
export function parseSubjectsByBill(emails: InvoiceEmailLike[]): Map<string, ParseSubjectLike> {
  const byBill = new Map<string, ParseSubjectLike>();
  for (const email of emails) {
    const billId = email.bill_id;
    if (!billId) continue;
    const existing = byBill.get(billId);
    const attachments = email.attachments ?? [];
    if (!existing) {
      byBill.set(billId, { bill_id: billId, assigned_at: email.assigned_at ?? null, attachments: [...attachments] });
      continue;
    }
    existing.attachments = [...(existing.attachments ?? []), ...attachments];
    // Newest assignment wins: assigned_at only feeds the pending-stall rule, and
    // an old one would call a just-reassigned bill stalled before its parse had
    // a chance to start.
    if (newer(email.assigned_at, existing.assigned_at)) existing.assigned_at = email.assigned_at ?? null;
  }
  return byBill;
}

const newer = (a: string | null | undefined, b: string | null | undefined): boolean => {
  if (!a) return false;
  if (!b) return true;
  return new Date(a).getTime() > new Date(b).getTime();
};

/**
 * What state this bill's invoice parse is in.
 *
 * `subject` is the bill's entry from `parseSubjectsByBill`, absent for a bill
 * whose invoice was uploaded by hand.
 */
export function billParseStateOf(
  bill: BillParseRowLike,
  subject: ParseSubjectLike | undefined,
  now: number = Date.now(),
): ParseState {
  const fromRow: ParseState = bill.actual_parsed_result
    ? 'parsed'
    : bill.invoice_path
      ? 'pending'
      : 'none';

  if (!subject) return fromRow;

  const fromEmail = parseStateOf(subject, now);
  // The parsed result lives on the bill row, so it exists whatever the
  // attachment says — an invoice re-parsed from the site page leaves the
  // original attachment untouched. Only the idle states defer to it: a parse
  // running right now is still running, and a failure is still a failure.
  if ((fromEmail === 'none' || fromEmail === 'pending') && fromRow === 'parsed') return 'parsed';
  if (fromEmail === 'none') return fromRow;
  return fromEmail;
}

/** Worst first, so the dropdown opens on the states someone has to act on. */
export const BILL_PARSE_ORDER: ParseState[] = [
  'failed', 'stalled', 'parsing', 'pending', 'parsed', 'none',
];

// Same colour vocabulary as the Invoices tab's chips, so a bill that reads
// "Parse failed" in one place does not read as something milder in the other.
// 'none' is the exception: on the Invoices tab it draws nothing, but a column
// cannot be blank, and "No invoice" is a normal state rather than a fault.
export const BILL_PARSE_BADGE_CLS: Record<ParseState, string> = {
  failed:  'bg-red-50 text-red-700',
  stalled: 'bg-amber-50 text-amber-700',
  parsing: 'bg-blue-50 text-blue-700',
  pending: 'bg-amber-50 text-amber-700',
  parsed:  'bg-green-50 text-green-700',
  none:    'bg-gray-100 text-gray-500',
};

export const BILL_PARSE_LABEL: Record<ParseState, string> = {
  failed: 'Parse failed',
  stalled: 'Parse stalled',
  parsing: 'Parsing',
  pending: 'Not parsed',
  parsed: 'Parsed',
  none: 'No invoice',
};
